/**
 * RTM — Retail Task Manager landing.
 *
 * v0.1.52 redesign — replaces the legacy "table-row + un-clickable KPI" layout
 * Bill flagged as broken UX ("SLA targets 18" — see TasksKpiStrip header).
 *
 * Audience model:
 *   - FSR              → personal queue (Mine), no upper KPI strip
 *   - Supervisor       → team queue + scope toggle, no upper KPI strip
 *   - Manager+         → all-branches scope by default, KPI strip with
 *                        clickable drilldowns
 *
 * KPIs (Manager+):
 *   - Open / SLA at risk / Breached today / Overdue / Unassigned / Critical
 *   - Each tile is clickable → filters card grid below
 *   - Catalog count "SLA targets N" REMOVED — that belongs in Admin only.
 *
 * Card grid (everyone):
 *   - Modern cards (TaskCard) with relative due, priority, SLA bar,
 *     quick actions (Complete / Note / Reassign).
 *   - Filters: All / Mine / Open / Due Today / Overdue / SLA at risk
 *   - Search: title or customer name
 *   - Scope toggle (Supervisor+): My team / Branch / All
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { RefreshCw, Loader2, Search } from '@retail-ops/panel-shell/fi';
import { Banner, Pill, FilterChips, Button } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import type { TaskRecordDto } from '@retail-ops/shared-types';
import type { TaskPriority, UserRole } from '@retail-ops/shared-types/enums';
import { TaskCard, deriveSlaState } from './components/TaskCard';
import { TasksKpiStrip } from './components/TasksKpiStrip';
import type { TasksKpiKey } from './components/TasksKpiStrip';
import { useTaskResolver } from '@/hooks/useTaskResolver';
import './rtm.css';

// ---------------------------------------------------------------------------
// Role gating — who sees the upper KPI strip
// ---------------------------------------------------------------------------
const KPI_STRIP_ROLES: ReadonlyArray<UserRole> = [
  'Manager',
  'Executive',
  'SuperAdmin',
];

// Roles that can use the team scope toggle (My team / Branch / All)
const SUPERVISORY_ROLES: ReadonlyArray<UserRole> = [
  'Supervisor',
  'Manager',
  'Executive',
  'SuperAdmin',
];

// Roles that can reassign tasks
const REASSIGN_ROLES: ReadonlyArray<UserRole> = [
  'Supervisor',
  'Manager',
  'Executive',
  'SuperAdmin',
];

function roleIn(role: UserRole | undefined, list: ReadonlyArray<UserRole>): boolean {
  return role ? list.includes(role) : false;
}

// ---------------------------------------------------------------------------
// Quick-filter chips
// ---------------------------------------------------------------------------
type QuickFilter = 'all' | 'mine' | 'open' | 'due-today' | 'overdue' | 'sla-risk';
type ScopeFilter = 'mine' | 'team' | 'branch' | 'all';

function isDueToday(iso?: string): boolean {
  if (!iso) return false;
  return iso.slice(0, 10) === new Date().toISOString().slice(0, 10);
}

function isOverdue(t: TaskRecordDto): boolean {
  if (!t.dueDate) return false;
  return new Date(t.dueDate).getTime() < Date.now() && t.status !== 'Completed';
}

// Map a KPI key → quick filter (so a KPI click drills the card grid)
function kpiToFilter(k: TasksKpiKey): QuickFilter {
  switch (k) {
    case 'sla-at-risk':
      return 'sla-risk';
    case 'breached-today':
    case 'overdue':
      return 'overdue';
    case 'open':
    case 'unassigned':
    case 'critical':
    default:
      return 'open';
  }
}

export default function RTM(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const taskResolver = useTaskResolver();

  // Scope: who/what to load. Manager+ default to 'all'; Supervisor 'team'; FSR 'mine'.
  const defaultScope: ScopeFilter = roleIn(user?.primaryRole, ['Manager', 'Executive', 'SuperAdmin'])
    ? 'all'
    : roleIn(user?.primaryRole, ['Supervisor'])
      ? 'team'
      : 'mine';

  const [scope, setScope] = useState<ScopeFilter>(defaultScope);
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [selectedKpi, setSelectedKpi] = useState<TasksKpiKey | null>(null);
  const [search, setSearch] = useState<string>('');
  const [tasks, setTasks] = useState<ReadonlyArray<TaskRecordDto>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const showKpiStrip = roleIn(user?.primaryRole, KPI_STRIP_ROLES);
  const showScopeToggle = roleIn(user?.primaryRole, SUPERVISORY_ROLES);
  const canReassign = roleIn(user?.primaryRole, REASSIGN_ROLES);

  // -------------------------------------------------------------------------
  // Load tasks
  // -------------------------------------------------------------------------
  const load = useCallback(async (): Promise<void> => {
    if (!user) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      // Server-side filter is intentionally LOOSE — we let the client narrow
      // because Manager+ KPIs need the full pool to compute (an Open-only
      // filter would zero out a "Completed today" comparison).
      const filters: Record<string, string | number | undefined> = { top: 200 };
      if (scope === 'mine') filters['assignedTo'] = user.upn;
      // 'team' | 'branch' | 'all' — server scopes to subordinates / branch /
      // tenant per role precedence; client just passes through.
      const resp = await apiClient.retail.tasks.list(filters as never, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      setTasks(resp.tasks);
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load tasks.');
      setTasks([]);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [apiClient, scope, user]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  // -------------------------------------------------------------------------
  // Derive filtered list (quick filter + search + KPI drilldown)
  // -------------------------------------------------------------------------
  const filtered = useMemo<ReadonlyArray<TaskRecordDto>>(() => {
    const myEmail = user?.upn ?? '';
    let list = tasks;

    // Quick filter chip
    switch (quickFilter) {
      case 'mine':
        list = list.filter((t) => t.assignedTo === myEmail);
        break;
      case 'open':
        list = list.filter((t) => t.status !== 'Completed' && t.status !== 'Cancelled');
        break;
      case 'due-today':
        list = list.filter((t) => isDueToday(t.dueDate));
        break;
      case 'overdue':
        list = list.filter(isOverdue);
        break;
      case 'sla-risk':
        list = list.filter((t) => deriveSlaState(t).state === 'at-risk');
        break;
      case 'all':
      default:
        // No additional filtering
        break;
    }

    // KPI drilldown narrows further on top of quick filter
    if (selectedKpi) {
      switch (selectedKpi) {
        case 'unassigned':
          list = list.filter((t) => {
            const a = (t.assignedTo ?? '').trim().toLowerCase();
            return a === '' || a === 'unassigned';
          });
          break;
        case 'critical':
          list = list.filter(
            (t) =>
              t.severity === 'critical' ||
              String(t.priority).toLowerCase() === 'urgent' ||
              String(t.priority).toLowerCase() === 'critical',
          );
          break;
        case 'breached-today':
          list = list.filter((t) => {
            const sla = deriveSlaState(t);
            return sla.state === 'breached' && t.dueDate && t.dueDate.slice(0, 10) === new Date().toISOString().slice(0, 10);
          });
          break;
        // 'open', 'overdue', 'sla-at-risk' already covered by quickFilter mapping
        default:
          break;
      }
    }

    // Search
    const q = search.trim().toLowerCase();
    if (q.length > 0) {
      list = list.filter((t) => {
        return (
          t.title.toLowerCase().includes(q) ||
          (t.customerName ?? '').toLowerCase().includes(q)
        );
      });
    }

    // Sort: overdue first, then due-soonest, then critical priority
    return [...list].sort((a, b) => {
      const aOver = isOverdue(a) ? 0 : 1;
      const bOver = isOverdue(b) ? 0 : 1;
      if (aOver !== bOver) return aOver - bOver;
      const aDue = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
      const bDue = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
      if (aDue !== bDue) return aDue - bDue;
      const aPri = String(a.priority).toLowerCase();
      const bPri = String(b.priority).toLowerCase();
      const order: Record<string, number> = { critical: 0, urgent: 0, high: 1, normal: 2, low: 3 };
      return (order[aPri] ?? 2) - (order[bPri] ?? 2);
    });
  }, [tasks, quickFilter, selectedKpi, search, user]);

  // KPI tile click → set drilldown + map to chip filter
  const handleKpiSelect = (k: TasksKpiKey): void => {
    if (selectedKpi === k) {
      setSelectedKpi(null);
      setQuickFilter('all');
      return;
    }
    setSelectedKpi(k);
    setQuickFilter(kpiToFilter(k));
  };

  const onOpenTask = (t: TaskRecordDto): void => taskResolver.open(t);
  const onCompleteTask = (t: TaskRecordDto): void => { void navigate(t.taskId); };
  const onReassignTask = (t: TaskRecordDto): void => { void navigate(t.taskId); };
  const onAddNote = (t: TaskRecordDto): void => { void navigate(t.taskId); };

  // Friendly empty-state message
  const emptyMessage = useMemo<string>(() => {
    if (loading) return '';
    if (tasks.length === 0) {
      return scope === 'mine'
        ? 'No tasks assigned to you. Nice work — your queue is clear.'
        : 'No tasks in this scope.';
    }
    if (search.trim().length > 0) return `No tasks match "${search}".`;
    if (selectedKpi || quickFilter !== 'all') return 'No tasks match the current filter.';
    return 'No tasks to show.';
  }, [tasks.length, scope, search, selectedKpi, quickFilter, loading]);

  return (
    <div className="rtm-page">
      <header className="rtm-page__header">
        <div className="rtm-page__title-row">
          <h1>Task Manager</h1>
          <Pill variant="muted">Module RTM</Pill>
          <Pill variant="info">{user?.primaryRole ?? '—'}</Pill>
          {scope !== 'mine' && (
            <Pill variant="muted">
              Scope:{' '}
              {scope === 'all' ? 'All branches' : scope === 'branch' ? 'Branch' : 'My team'}
            </Pill>
          )}
        </div>
        <p className="rtm-page__scope">
          {scope === 'mine'
            ? 'Your personal task queue. Click a card to act on it.'
            : 'Team task workspace. Click a KPI to drill into a filtered list.'}
        </p>
      </header>

      {showKpiStrip && (
        <TasksKpiStrip
          tasks={tasks}
          onSelect={handleKpiSelect}
          selected={selectedKpi}
        />
      )}

      <div className="rtm-toolbar">
        <FilterChips<QuickFilter>
          label="Quick filter"
          options={[
            { value: 'all', label: 'All' },
            { value: 'mine', label: 'Mine' },
            { value: 'open', label: 'Open' },
            { value: 'due-today', label: 'Due today' },
            { value: 'overdue', label: 'Overdue' },
            { value: 'sla-risk', label: 'SLA at risk' },
          ]}
          selected={quickFilter}
          onChange={(v) => {
            setQuickFilter(v);
            // If user picks a chip manually, drop any KPI selection that
            // doesn't match — keeps the two in agreement.
            if (selectedKpi && kpiToFilter(selectedKpi) !== v) {
              setSelectedKpi(null);
            }
          }}
        />

        <div className="rtm-search">
          <Search size={12} aria-hidden />
          <input
            type="search"
            placeholder="Search task or customer name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search tasks"
          />
        </div>

        {showScopeToggle && (
          <div className="rtm-scope">
            <span className="rtm-scope__label">Scope</span>
            <div className="rtm-scope__group" role="group" aria-label="Task scope">
              {(['mine', 'team', 'branch', 'all'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`rtm-scope__btn ${scope === s ? 'is-active' : ''}`}
                  onClick={() => setScope(s)}
                  aria-pressed={scope === s ? 'true' : 'false'}
                >
                  {s === 'mine'
                    ? 'Mine'
                    : s === 'team'
                      ? 'Team'
                      : s === 'branch'
                        ? 'Branch'
                        : 'All'}
                </button>
              ))}
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          size="sm"
          startIcon={loading ? <Loader2 size={12} /> : <RefreshCw size={12} />}
          onClick={() => void load()}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      {filtered.length === 0 && !loading && (
        <div className="rtm-empty">{emptyMessage}</div>
      )}

      {filtered.length > 0 && (
        <div className="task-card-grid">
          {filtered.map((t) => (
            <TaskCard
              key={t.taskId}
              task={t}
              onOpen={onOpenTask}
              onComplete={onCompleteTask}
              onReassign={onReassignTask}
              onAddNote={onAddNote}
              canReassign={canReassign}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Re-export so module consumers keep working.
export type { TaskPriority };
